// utils/denyLogger.ts
//
// Diagnostic: capture Firebase RTDB "permission_denied" events across ALL
// clients and report aggregated per-path-pattern counts to a central node that
// admins can read. Off by default; an admin flips config/denyLogging/enabled
// before an event and reads the results in the Admin dashboard afterwards.
//
// How it captures denies without touching every subscription site: the RTDB
// SDK logs "FIREBASE WARNING: permission_denied at /path: ..." to the console.
// We wrap console.warn/error, parse out the path, normalise dynamic ids into a
// pattern (e.g. /tournaments/{id}/...), and aggregate. No data is billed and no
// app behaviour changes — it only listens.

import { ref, onValue, update, serverTimestamp, Unsubscribe } from 'firebase/database';
import { db } from '../config/firebase';

export const DENY_FLAG_PATH = 'config/denyLogging/enabled';
export const DENY_LOG_PATH = 'denyLog';

interface DenyRecord {
  pattern: string;
  count: number;
  last: number;
  sample: string;
}

const store = new Map<string, DenyRecord>();
let active = false;
let dirty = false;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let currentUid: string | null = null;
let currentName = 'Staff';
let origWarn: typeof console.warn | null = null;
let origError: typeof console.error | null = null;

// Matches the RTDB warning text. Path runs until whitespace or the ':' before
// the explanatory sentence.
const DENY_RE = /permission_denied at (\/[^\s:]+)/i;
const MAX_PATTERNS = 200;
const FLUSH_MS = 8000;

/** Collapse dynamic path segments (push ids, uids, numbers) into placeholders
 *  so denies group by shape rather than by individual record. */
export function normalizeDenyPath(path: string): string {
  return path
    .split('/')
    .map((seg) => {
      if (!seg) return seg;
      if (/^-[A-Za-z0-9_-]{12,}$/.test(seg)) return '{id}'; // push id
      if (/^[A-Za-z0-9]{20,}$/.test(seg)) return '{id}'; // uid / long token
      if (/^\d+$/.test(seg)) return '{n}'; // numeric index
      return seg;
    })
    .join('/');
}

function record(path: string): void {
  // Never log our own diagnostic paths (would feed back into itself).
  if (path.startsWith('/denyLog') || path.startsWith('/config/denyLogging')) return;
  const pattern = normalizeDenyPath(path);
  const existing = store.get(pattern);
  if (existing) {
    existing.count += 1;
    existing.last = Date.now();
  } else if (store.size < MAX_PATTERNS) {
    store.set(pattern, { pattern, count: 1, last: Date.now(), sample: path });
  }
  dirty = true;
}

function scan(args: unknown[]): void {
  for (const a of args) {
    if (typeof a === 'string') {
      const m = a.match(DENY_RE);
      if (m) record(m[1]);
    }
  }
}

function startIntercept(): void {
  if (origWarn) return;
  origWarn = console.warn.bind(console);
  origError = console.error.bind(console);
  console.warn = (...args: unknown[]) => {
    try {
      scan(args);
    } catch {
      /* never break logging */
    }
    (origWarn as (...a: unknown[]) => void)(...args);
  };
  console.error = (...args: unknown[]) => {
    try {
      scan(args);
    } catch {
      /* never break logging */
    }
    (origError as (...a: unknown[]) => void)(...args);
  };
}

function stopIntercept(): void {
  if (origWarn) console.warn = origWarn;
  if (origError) console.error = origError;
  origWarn = null;
  origError = null;
}

function sanitizeKey(p: string): string {
  return p.replace(/[/.#$[\]{}]/g, '_').slice(0, 500) || '_root';
}

async function flush(): Promise<void> {
  if (!dirty || !currentUid || store.size === 0) return;
  dirty = false;
  const patterns: Record<string, { p: string; c: number; last: number }> = {};
  for (const r of store.values()) {
    patterns[sanitizeKey(r.pattern)] = { p: r.pattern, c: r.count, last: r.last };
  }
  try {
    await update(ref(db, `${DENY_LOG_PATH}/${currentUid}`), {
      name: currentName,
      updatedAt: serverTimestamp(),
      patterns,
    });
  } catch {
    // The write itself was denied or we're offline — keep the data and retry.
    dirty = true;
  }
}

/** Begin capturing + reporting denies for this client/user. Idempotent. */
export function startDenyLogging(uid: string, name: string): void {
  currentUid = uid;
  currentName = name || 'Staff';
  if (active) return;
  active = true;
  startIntercept();
  flushTimer = setInterval(() => {
    void flush();
  }, FLUSH_MS);
}

/** Stop capturing and flush whatever's buffered. Idempotent. */
export function stopDenyLogging(): void {
  if (!active) return;
  active = false;
  stopIntercept();
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  void flush();
}

/** Subscribe to the global on/off flag. Fail-closed (off) on read error. */
export function subscribeDenyFlag(cb: (enabled: boolean) => void): Unsubscribe {
  return onValue(
    ref(db, DENY_FLAG_PATH),
    (s) => cb(s.val() === true),
    () => cb(false),
  );
}
